import React, { Component } from 'react';

import SidebarComponent from '../components/SidebarComponent';
import NavbarComponent from '../components/NavbarComponent';
import HeaderComponent from '../components/HeaderComponent';
import CashCheckEditor from '../components/CashCheckEditor';
import { validateSession } from '../utils/functions';
import { navigateTo } from '../utils/navigation';
import { get, put } from '../utils/apiClient';
import {
    MEDIOS, MEDIOS_PLATA, ORIGENES, FACTURA, MOVIMIENTOS, MOVIMIENTOS_SALIDA, CUENTA_EQUIPO,
    pesos, diferencia, hayDiferencia, hora, fecha, fechaConDia, duracion, estadoTicket,
    filtrarTickets, totalCobrado, porOrigenYMedio, sinPlata, estadoLinea, pendientesDeConciliar,
    transferenciasConComprobante,
} from '../utils/cashControl';

const ROLE_ADMIN = 1;

// Paleta explícita (mismo criterio que el cierre): las clases `alert-*` del template tiñen todo y
// acá el texto es lo que hay que leer.
const C = {
    malo: '#a8402f', maloFondo: '#fbefec',
    sobra: '#8a6212', sobraFondo: '#fdf6e7',
    bien: '#2c7a5a', bienFondo: '#ecf6f1',
    info: '#3d5170', infoFondo: '#eef2f7',
    tenue: '#6b7680', fuerte: '#1b232c', borde: '#e1e5eb', fila: '#fafbfc',
};

const NIVEL = {
    alta: { color: C.malo, fondo: C.maloFondo, texto: 'Importante' },
    media: { color: C.sobra, fondo: C.sobraFondo, texto: 'Revisar' },
    info: { color: C.info, fondo: C.infoFondo, texto: 'Para saber' },
};

const ESTADO_LINEA = {
    cuadra: { color: C.bien, fondo: C.bienFondo, texto: 'Cuadra' },
    descuadra: { color: C.malo, fondo: C.maloFondo, texto: 'No cuadra' },
    conciliada: { color: C.bien, fondo: C.bienFondo, texto: 'Conciliada' },
    con_diferencia: { color: C.malo, fondo: C.maloFondo, texto: 'Con diferencia' },
    informativa: { color: C.info, fondo: C.infoFondo, texto: 'Informativa' },
};

const FILTROS_VACIOS = { medio: '', origen: '', lector: '', estado: '', texto: '', ids: null, idsLabel: '' };

const Pill = ({ color, fondo, children, title }) => (
    <span
        title={title}
        style={{
            display: 'inline-block', padding: '2px 8px', borderRadius: 999, fontSize: 12, fontWeight: 600,
            color, background: fondo, whiteSpace: 'nowrap',
        }}
    >
        {children}
    </span>
);

const num = { fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' };

/**
 * Control de caja (backend docs/spec-control-de-caja.md): el detalle fino de una sesión de caja
 * para el admin, y la conciliación de lo que declaró quien cerró.
 *
 * Ruta de UN nivel con la sesión en la query (`/controlCaja?sesion=123`) a propósito: el CSS del
 * template se carga con rutas relativas, y una ruta de dos niveles abierta en frío (un F5) sale sin
 * estilos (ver EmailLogDetailPage).
 */
class CashControlPage extends Component {

    constructor(props) {
        super(props);
        this.state = {
            checked: !!localStorage.getItem('token'),
            role: Number(localStorage.getItem('role') || 0),
            loading: true,
            refreshing: false,
            error: null,
            data: null,
            filtros: FILTROS_VACIOS,
            abiertos: {},
            editando: null,
            guardando: false,
            errorMarca: null,
            notaRevision: '',
            revisando: false,
            verMatriz: true,
        };
        this.refs_ = {
            alertas: React.createRef(),
            lineas: React.createRef(),
            transferencias: React.createRef(),
            ventas: React.createRef(),
            movimientos: React.createRef(),
            sinCaja: React.createRef(),
            mp: React.createRef(),
        };
    }

    async componentDidMount() {
        const role = await validateSession(true);
        this.setState({ checked: true, role });
        if (Number(role) === ROLE_ADMIN) {
            this.cargar();
        }
    }

    componentDidUpdate(prevProps) {
        if (prevProps.location.search !== this.props.location.search) {
            this.setState({ filtros: FILTROS_VACIOS, abiertos: {}, editando: null, data: null, loading: true });
            this.cargar();
            window.scrollTo(0, 0);
        }
    }

    sessionId = () => new URLSearchParams(this.props.location.search).get('sesion');

    cargar = async ({ silencioso = false } = {}) => {
        const id = this.sessionId();
        if (!id) {
            this.setState({ loading: false, error: 'Falta indicar qué caja ver.' });
            return;
        }
        this.setState(silencioso ? { refreshing: true } : { loading: true, error: null });
        try {
            const data = await get(`cashSession/${encodeURIComponent(id)}/control`);
            this.setState({
                data, loading: false, refreshing: false, error: null,
                notaRevision: (data.session && data.session.review_note) || '',
            });
        } catch (e) {
            this.setState({ loading: false, refreshing: false, error: e.message });
        }
    };

    ir = (id) => navigateTo(`/controlCaja?sesion=${id}`);

    scrollA = (clave) => {
        const ref = this.refs_[clave];
        if (ref && ref.current) ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    // ── Marcas ────────────────────────────────────────────────────────────────────────────────

    guardarMarca = async (kind, ref, valores) => {
        this.setState({ guardando: true, errorMarca: null });
        try {
            await put(`cashSession/${this.state.data.session.id}/check`, { kind, ref, ...valores });
            this.setState({ guardando: false, editando: null });
            await this.cargar({ silencioso: true });
        } catch (e) {
            this.setState({ guardando: false, errorMarca: e.message });
        }
    };

    marcarRapido = (kind, ref, status) => this.guardarMarca(kind, ref, {
        status, verified_amount: null, note: status === 'ok' ? 'Está en el banco' : '',
    });

    revisar = async (reviewed) => {
        this.setState({ revisando: true });
        try {
            await put(`cashSession/${this.state.data.session.id}/review`, { reviewed, note: this.state.notaRevision });
            this.setState({ revisando: false });
            await this.cargar({ silencioso: true });
        } catch (e) {
            this.setState({ revisando: false });
            alert(e.message);
        }
    };

    verEnVentas = (ids, label) => {
        this.setState({ filtros: { ...FILTROS_VACIOS, ids, idsLabel: label } }, () => this.scrollA('ventas'));
    };

    // ── Encabezado ────────────────────────────────────────────────────────────────────────────

    renderEncabezado() {
        const { session, nav, lines, transfers } = this.state.data;
        const cerrada = session.status === 'cerrada';
        const revisada = !!session.reviewed_at;
        const pendientes = pendientesDeConciliar(lines, transfers);
        const dato = (etiqueta, valor, sub) => (
            <div className="col-6 col-md-3 mb-3">
                <div style={{ fontSize: 12, color: C.tenue }}>{etiqueta}</div>
                <div style={{ fontWeight: 600, color: C.fuerte }}>{valor || '—'}</div>
                {sub && <div style={{ fontSize: 12, color: C.tenue }}>{sub}</div>}
            </div>
        );

        return (
            <div className="card card-small mb-4">
                <div className="card-header border-bottom d-flex flex-wrap justify-content-between align-items-center" style={{ gap: 8 }}>
                    <div>
                        <span style={{ fontSize: 17, fontWeight: 600, color: C.fuerte, textTransform: 'capitalize' }}>
                            {fechaConDia(session.opened_at)}
                        </span>
                        <span style={{ color: C.tenue, marginLeft: 8 }}>Caja #{session.id}{session.branch_name ? ` · ${session.branch_name}` : ''}</span>
                        <span style={{ marginLeft: 10 }}>
                            {!cerrada && <Pill color={C.info} fondo={C.infoFondo}>Abierta · en vivo</Pill>}
                            {cerrada && revisada && <Pill color={C.bien} fondo={C.bienFondo}>Revisada</Pill>}
                            {cerrada && !revisada && <Pill color={C.sobra} fondo={C.sobraFondo}>Sin revisar</Pill>}
                        </span>
                    </div>
                    <div className="d-flex" style={{ gap: 6 }}>
                        <button className="btn btn-sm btn-white" disabled={!nav.prev_id} onClick={() => this.ir(nav.prev_id)}>‹ Anterior</button>
                        <button className="btn btn-sm btn-white" onClick={() => navigateTo('/caja')}>Historial</button>
                        <button className="btn btn-sm btn-white" disabled={!nav.next_id} onClick={() => this.ir(nav.next_id)}>Siguiente ›</button>
                    </div>
                </div>
                <div className="card-body pb-1">
                    <div className="row">
                        {dato('Abrió', session.opened_by_name, `${hora(session.opened_at)} · ${session.opened_by || ''}`)}
                        {dato('Cerró', cerrada ? session.closed_by_name : 'Todavía abierta', cerrada ? `${fecha(session.closed_at) !== fecha(session.opened_at) ? `${fecha(session.closed_at)} ` : ''}${hora(session.closed_at)} · ${session.closed_by || ''}` : null)}
                        {dato('Duración', duracion(session.opened_at, cerrada ? session.closed_at : null) === '—' && !cerrada ? 'En curso' : duracion(session.opened_at, session.closed_at))}
                        {dato('Fondo de apertura', pesos(session.opening_float))}
                    </div>
                    {session.observations && (
                        <div className="mb-3" style={{ borderLeft: `3px solid ${C.borde}`, paddingLeft: 12, color: C.fuerte }}>
                            <div style={{ fontSize: 12, color: C.tenue }}>Observaciones de quien cerró</div>
                            «{session.observations}»
                        </div>
                    )}
                    {!session.has_snapshot && cerrada && (
                        <div className="mb-3" style={{ fontSize: 13, color: C.tenue }}>
                            Caja cerrada antes del control de caja: no hay foto del cierre, así que no se pueden detectar
                            cambios posteriores ticket por ticket, y las transferencias no se tildaron una por una.
                        </div>
                    )}
                </div>
                {cerrada && (
                    <div className="card-footer border-top" style={{ background: revisada ? C.bienFondo : '#fff' }}>
                        {revisada ? (
                            <div className="d-flex flex-wrap justify-content-between align-items-center" style={{ gap: 8 }}>
                                <div style={{ color: C.bien }}>
                                    <strong>Revisada</strong> por {session.reviewed_by} el {fecha(session.reviewed_at)} a las {hora(session.reviewed_at)}
                                    {session.review_note && <span style={{ color: C.fuerte }}> — «{session.review_note}»</span>}
                                </div>
                                <button className="btn btn-sm btn-white" disabled={this.state.revisando} onClick={() => this.revisar(false)}>
                                    Desmarcar
                                </button>
                            </div>
                        ) : (
                            <div className="d-flex flex-wrap align-items-center" style={{ gap: 8 }}>
                                <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    style={{ flex: 1, minWidth: 240 }}
                                    maxLength={500}
                                    placeholder="Nota de la revisión (opcional)"
                                    value={this.state.notaRevision}
                                    onChange={(e) => this.setState({ notaRevision: e.target.value })}
                                />
                                <button className="btn btn-sm btn-primary" disabled={this.state.revisando} onClick={() => this.revisar(true)}>
                                    Marcar la caja como revisada
                                </button>
                                {pendientes > 0 && (
                                    <span style={{ fontSize: 13, color: C.sobra }}>
                                        Quedan {pendientes} {pendientes === 1 ? 'cosa' : 'cosas'} sin conciliar.
                                    </span>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    }

    // ── Alertas ───────────────────────────────────────────────────────────────────────────────

    accionAlerta(alerta) {
        const refs = alerta.refs || {};
        if (refs.tickets && refs.tickets.length) {
            return <button className="btn btn-sm btn-link p-0" onClick={() => this.verEnVentas(refs.tickets, alerta.title)}>Ver las ventas</button>;
        }
        if (refs.transfers) return <button className="btn btn-sm btn-link p-0" onClick={() => this.scrollA('transferencias')}>Ir a las transferencias</button>;
        if (refs.movements) return <button className="btn btn-sm btn-link p-0" onClick={() => this.scrollA('movimientos')}>Ir a los movimientos</button>;
        if (refs.unsealed) return <button className="btn btn-sm btn-link p-0" onClick={() => this.scrollA('sinCaja')}>Ver cuáles</button>;
        if (refs.mp) return <button className="btn btn-sm btn-link p-0" onClick={() => this.scrollA('mp')}>Ver los cobros</button>;
        if (refs.line) return <button className="btn btn-sm btn-link p-0" onClick={() => this.scrollA('lineas')}>Ir a la conciliación</button>;
        return null;
    }

    renderAlertas() {
        const { alerts } = this.state.data;
        return (
            <div className="card card-small mb-4" ref={this.refs_.alertas}>
                <div className="card-header border-bottom">
                    <span style={{ fontWeight: 600 }}>Para revisar</span>
                    <span style={{ color: C.tenue, marginLeft: 8, fontSize: 13 }}>
                        {alerts.length === 0 ? 'nada fuera de lugar' : `${alerts.length} ${alerts.length === 1 ? 'punto' : 'puntos'}`}
                    </span>
                </div>
                <div className="card-body py-2">
                    {alerts.length === 0 && (
                        <div style={{ color: C.bien, padding: '6px 0' }}>
                            No hay nada fuera de lugar en esta caja: cuadra el efectivo, los lotes y las transferencias.
                        </div>
                    )}
                    {alerts.map((a, i) => {
                        const n = NIVEL[a.level] || NIVEL.info;
                        return (
                            <div
                                key={`${a.code}-${i}`}
                                style={{
                                    borderLeft: `4px solid ${n.color}`, background: n.fondo, borderRadius: 4,
                                    padding: '8px 12px', margin: '8px 0',
                                }}
                            >
                                <div className="d-flex flex-wrap justify-content-between" style={{ gap: 8 }}>
                                    <div style={{ fontWeight: 600, color: C.fuerte }}>{a.title}</div>
                                    <span style={{ fontSize: 11, letterSpacing: '.06em', textTransform: 'uppercase', color: n.color, fontWeight: 700 }}>{n.texto}</span>
                                </div>
                                {a.detail && <div style={{ fontSize: 13, color: C.fuerte, marginTop: 2 }}>{a.detail}</div>}
                                <div style={{ marginTop: 2 }}>{this.accionAlerta(a)}</div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }

    // ── Conciliación ──────────────────────────────────────────────────────────────────────────

    celdaSistema(l) {
        // Si el recálculo de hoy no coincide con el esperado al cierre, algo se tocó después.
        const cambio = this.state.data.session.status === 'cerrada' && hayDiferencia((l.system_now || 0) - (l.system || 0));
        return (
            <div style={num}>
                {pesos(l.system)}
                {l.key === 'transferencias' && <span style={{ color: C.tenue }}> · {l.count_system}</span>}
                {cambio && (
                    <div style={{ fontSize: 12, color: C.malo }} title="Recalculado con los datos de hoy">
                        hoy da {pesos(l.system_now)}
                    </div>
                )}
            </div>
        );
    }

    celdaDeclarado(l) {
        if (l.key === 'transferencias') {
            const verificadas = l.verified_by_admin > 0 && (
                <div style={{ fontSize: 12, color: C.bien }}>+{l.verified_by_admin} {l.verified_by_admin === 1 ? 'verificada' : 'verificadas'} en el banco</div>
            );
            if (l.ticked_at_close !== null && l.ticked_at_close !== undefined) {
                return <span style={num}>{l.ticked_at_close} de {l.count_system} tildadas{verificadas}</span>;
            }
            return l.count_declared == null ? '—' : <span style={num}>{l.count_declared} comprobantes{verificadas}</span>;
        }
        if (l.key === 'mercadopago') {
            return <span style={num} title="Lo que Mercado Pago registra como cobrado y vinculado a estas ventas">{pesos(l.mp_verified)} según MP</span>;
        }
        if (l.key === 'tarjeta_sin_lector') {
            return <span style={{ fontSize: 13, color: C.tenue }}>está dentro de algún lote</span>;
        }
        return <span style={num}>{pesos(l.declared)}</span>;
    }

    celdaDiferencia(l) {
        let valor = l.difference;
        if (l.key === 'transferencias') {
            const declaradas = transferenciasConComprobante(l);
            if (declaradas == null) return '—';
            const faltan = l.count_system - declaradas;
            if (faltan === 0) return <span style={{ color: C.bien }}>0</span>;
            return <span style={{ color: C.malo, fontWeight: 600 }}>{faltan > 0 ? `${faltan} sin comprobante` : `${-faltan} de más`}</span>;
        }
        if (valor === null || valor === undefined) return '—';
        const color = !hayDiferencia(valor) ? C.bien : (valor > 0 ? C.sobra : C.malo);
        return (
            <span style={{ ...num, color, fontWeight: hayDiferencia(valor) ? 600 : 400 }}>
                {diferencia(valor)}
                {hayDiferencia(valor) && l.kind !== 'informativo' && <span style={{ fontWeight: 400 }}> {valor > 0 ? 'sobra' : 'falta'}</span>}
            </span>
        );
    }

    renderLineas() {
        const { lines, session } = this.state.data;
        const cerrada = session.status === 'cerrada';
        const efectivo = lines.find((l) => l.key === 'efectivo');
        const b = efectivo.breakdown || {};

        return (
            <div className="card card-small mb-4" ref={this.refs_.lineas}>
                <div className="card-header border-bottom">
                    <span style={{ fontWeight: 600 }}>Conciliación</span>
                    <span style={{ color: C.tenue, marginLeft: 8, fontSize: 13 }}>
                        {cerrada ? 'lo que esperaba el sistema al cierre contra lo que declaró quien cerró' : 'la caja sigue abierta: el esperado es el de ahora y todavía no hay nada declarado'}
                    </span>
                </div>
                <div className="card-body p-0">
                    <div style={{ overflowX: 'auto' }}>
                        <table className="table mb-0" style={{ fontSize: 14 }}>
                            <thead className="bg-light">
                                <tr>
                                    <th>Línea</th>
                                    <th className="text-right">Sistema</th>
                                    <th className="text-right">Declarado</th>
                                    <th className="text-right">Diferencia</th>
                                    <th>Conciliación</th>
                                </tr>
                            </thead>
                            <tbody>
                                {lines.map((l) => {
                                    const estado = ESTADO_LINEA[estadoLinea(l)];
                                    const editando = this.state.editando === `linea:${l.key}`;
                                    return (
                                        <React.Fragment key={l.key}>
                                            <tr>
                                                <td>
                                                    <div style={{ fontWeight: 600, color: C.fuerte }}>{l.label}</div>
                                                    {l.key === 'efectivo' && (
                                                        <div style={{ fontSize: 12, color: C.tenue }}>
                                                            fondo {pesos(b.opening_float)} + ventas {pesos(b.cash_sales)}
                                                            {b.employee_cash ? ` + cobranzas del equipo ${pesos(b.employee_cash)}` : ''}
                                                            {b.movements_in ? ` + ingresos ${pesos(b.movements_in)}` : ''}
                                                            {b.movements_out ? ` − retiros y gastos ${pesos(b.movements_out)}` : ''}
                                                        </div>
                                                    )}
                                                    {l.kind === 'lote' && (
                                                        <button className="btn btn-link btn-sm p-0" style={{ fontSize: 12 }}
                                                            onClick={() => this.setState({ filtros: { ...FILTROS_VACIOS, medio: '5', lector: String(l.pos_device_id) } }, () => this.scrollA('ventas'))}>
                                                            ver los cobros de este lector
                                                        </button>
                                                    )}
                                                    {l.key === 'tarjeta_sin_lector' && (
                                                        <button className="btn btn-link btn-sm p-0" style={{ fontSize: 12 }}
                                                            onClick={() => this.setState({ filtros: { ...FILTROS_VACIOS, medio: '5', lector: 'sin' } }, () => this.scrollA('ventas'))}>
                                                            ver cuáles son
                                                        </button>
                                                    )}
                                                </td>
                                                <td className="text-right">{this.celdaSistema(l)}</td>
                                                <td className="text-right">{this.celdaDeclarado(l)}</td>
                                                <td className="text-right">{this.celdaDiferencia(l)}</td>
                                                <td style={{ minWidth: 180, maxWidth: 280 }}>
                                                    <Pill color={estado.color} fondo={estado.fondo}>{estado.texto}</Pill>
                                                    {l.check && l.check.status && (
                                                        <div style={{ fontSize: 12, color: C.tenue, marginTop: 4 }}>
                                                            {l.check.verified_amount != null && <span>verificado {pesos(l.check.verified_amount)} · </span>}
                                                            {l.check.note && <span>«{l.check.note}» · </span>}
                                                            {l.check.reviewed_by}
                                                        </div>
                                                    )}
                                                    {cerrada && !editando && (
                                                        <div>
                                                            <button className="btn btn-link btn-sm p-0" onClick={() => this.setState({ editando: `linea:${l.key}`, errorMarca: null })}>
                                                                {l.check && l.check.status ? 'Cambiar' : 'Conciliar'}
                                                            </button>
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                            {editando && (
                                                <tr>
                                                    <td colSpan="5" style={{ borderTop: 0 }}>
                                                        <CashCheckEditor
                                                            check={l.check}
                                                            saving={this.state.guardando}
                                                            error={this.state.errorMarca}
                                                            amountLabel={l.kind === 'lote' ? 'Monto de la liquidación' : (l.key === 'transferencias' ? 'Monto en el banco' : 'Monto verificado')}
                                                            onSave={(valores) => this.guardarMarca('linea', l.key, valores)}
                                                            onClear={() => this.guardarMarca('linea', l.key, { status: null })}
                                                            onCancel={() => this.setState({ editando: null, errorMarca: null })}
                                                        />
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        );
    }

    // ── Transferencias ────────────────────────────────────────────────────────────────────────

    renderTransferencias() {
        const { transfers, session } = this.state.data;
        const cerrada = session.status === 'cerrada';
        if (transfers.length === 0) return null;

        return (
            <div className="card card-small mb-4" ref={this.refs_.transferencias}>
                <div className="card-header border-bottom">
                    <span style={{ fontWeight: 600 }}>Transferencias</span>
                    <span style={{ color: C.tenue, marginLeft: 8, fontSize: 13 }}>
                        una por comprobante · {pesos(transfers.filter((t) => t.still_counted).reduce((s, t) => s + t.amount, 0))}
                    </span>
                </div>
                <div className="card-body p-0">
                    <div style={{ overflowX: 'auto' }}>
                        <table className="table mb-0" style={{ fontSize: 14 }}>
                            <thead className="bg-light">
                                <tr>
                                    <th>Hora</th>
                                    <th>De</th>
                                    <th className="text-right">Monto</th>
                                    <th>En el cierre</th>
                                    <th>Contra el banco</th>
                                </tr>
                            </thead>
                            <tbody>
                                {transfers.map((t) => {
                                    const c = t.check || {};
                                    const editando = this.state.editando === `transferencia:${t.ref}`;
                                    return (
                                        <React.Fragment key={t.ref}>
                                            <tr style={{ opacity: t.still_counted ? 1 : 0.6 }}>
                                                <td style={num}>{hora(t.at)}</td>
                                                <td>
                                                    <div>{t.who || (t.source === 'cuenta_equipo' ? 'Cuenta del equipo' : 'Consumidor final')}</div>
                                                    <div style={{ fontSize: 12, color: C.tenue }}>
                                                        {t.source === 'cuenta_equipo' ? `Pago de la cuenta del equipo${t.reference ? ` · ref. ${t.reference}` : ''}` : `Venta #${t.purchaseId || '—'}`}
                                                        {!t.still_counted && ' · ya no está cobrada (cambió después del cierre)'}
                                                    </div>
                                                </td>
                                                <td className="text-right" style={num}>{pesos(t.amount)}</td>
                                                <td>
                                                    {c.checked_at_close === true && <Pill color={C.bien} fondo={C.bienFondo}>✓ con comprobante</Pill>}
                                                    {c.checked_at_close === false && <Pill color={C.malo} fondo={C.maloFondo}>sin tildar</Pill>}
                                                    {(c.checked_at_close === null || c.checked_at_close === undefined) && (
                                                        t.changed_after_close
                                                            ? <span style={{ fontSize: 13, color: C.malo }}>pasó a transferencia después del cierre</span>
                                                            : <span style={{ color: C.tenue }}>—</span>
                                                    )}
                                                </td>
                                                <td style={{ minWidth: 220 }}>
                                                    {c.status === 'ok' && <Pill color={C.bien} fondo={C.bienFondo}>Está en el banco</Pill>}
                                                    {c.status === 'diferencia' && <Pill color={C.malo} fondo={C.maloFondo}>Con diferencia</Pill>}
                                                    {c.status && c.note && <div style={{ fontSize: 12, color: C.tenue }}>«{c.note}» · {c.reviewed_by}</div>}
                                                    {cerrada && !c.status && !editando && (
                                                        <span>
                                                            <button className="btn btn-sm btn-white mr-1" disabled={this.state.guardando}
                                                                onClick={() => this.marcarRapido('transferencia', t.ref, 'ok')}>Está</button>
                                                            <button className="btn btn-sm btn-white" disabled={this.state.guardando}
                                                                onClick={() => this.setState({ editando: `transferencia:${t.ref}`, errorMarca: null })}>No aparece…</button>
                                                        </span>
                                                    )}
                                                    {cerrada && c.status && !editando && (
                                                        <button className="btn btn-link btn-sm p-0" onClick={() => this.setState({ editando: `transferencia:${t.ref}`, errorMarca: null })}>Cambiar</button>
                                                    )}
                                                </td>
                                            </tr>
                                            {editando && (
                                                <tr>
                                                    <td colSpan="5" style={{ borderTop: 0 }}>
                                                        <CashCheckEditor
                                                            check={c.status ? c : { status: 'diferencia' }}
                                                            saving={this.state.guardando}
                                                            error={this.state.errorMarca}
                                                            amountLabel="Monto en el banco"
                                                            onSave={(valores) => this.guardarMarca('transferencia', t.ref, valores)}
                                                            onClear={() => this.guardarMarca('transferencia', t.ref, { status: null })}
                                                            onCancel={() => this.setState({ editando: null, errorMarca: null })}
                                                        />
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        );
    }

    // ── Ventas ────────────────────────────────────────────────────────────────────────────────

    setFiltro = (campo, valor) => this.setState({ filtros: { ...this.state.filtros, [campo]: valor, ids: null, idsLabel: '' } });

    toggleTicket = (id) => this.setState({ abiertos: { ...this.state.abiertos, [id]: !this.state.abiertos[id] } });

    nombreLector = (id) => {
        if (id == null) return null;
        const linea = this.state.data.lines.find((l) => l.key === `lector:${id}`);
        return linea ? linea.label.replace(/^Lector\s+/i, '') : `#${id}`;
    };

    renderDetalleTicket(t) {
        return (
            <tr>
                <td colSpan="9" style={{ background: C.fila, borderTop: 0, padding: '8px 16px 14px' }}>
                    {t.changes && t.changes.length > 0 && (
                        <div style={{ color: C.malo, fontSize: 13, marginBottom: 6 }}>
                            <strong>Cambió después del cierre:</strong> {t.changes.join(' · ')}
                        </div>
                    )}
                    <table className="table table-sm mb-2" style={{ fontSize: 13, background: '#fff' }}>
                        <thead>
                            <tr>
                                <th>Producto</th>
                                <th className="text-right">Cant.</th>
                                <th className="text-right">Unitario</th>
                                <th className="text-right">Desc.</th>
                                <th className="text-right">Subtotal</th>
                            </tr>
                        </thead>
                        <tbody>
                            {t.lines.length === 0 && (
                                <tr><td colSpan="5" style={{ color: C.tenue }}>Sin líneas registradas.</td></tr>
                            )}
                            {t.lines.map((l, i) => (
                                <tr key={`${l.productCode}-${i}`}>
                                    <td>{l.name}</td>
                                    <td className="text-right" style={num}>{l.quantity}</td>
                                    <td className="text-right" style={num}>{pesos(l.price)}</td>
                                    <td className="text-right" style={num}>{l.discount ? `${Math.round(l.discount * 100) / 100}%` : '—'}</td>
                                    <td className="text-right" style={num}>{pesos(l.price * l.quantity * (1 - l.discount / 100))}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <div className="d-flex flex-wrap" style={{ gap: '4px 18px', fontSize: 13, color: C.fuerte }}>
                        <span>Ticket #{t.ticketId}</span>
                        {t.discount_code && <span>Código: <strong>{t.discount_code}</strong></span>}
                        {t.discount_amount > 0 && <span>Descuento: {pesos(t.discount_amount)}</span>}
                        {t.store_credit_applied > 0 && <span>Saldo a favor usado: {pesos(t.store_credit_applied)}</span>}
                        {t.gift_card_applied > 0 && <span>Gift card usada: {pesos(t.gift_card_applied)}</span>}
                        {t.invoiceId && <span>CFE: {t.invoiceId}</span>}
                        {t.cedula && t.cedula !== 'CONSUMIDOR_FINAL' && <span>Cédula: {t.cedula}</span>}
                        {t.mp.map((m) => (
                            <span key={m.payment_id}>
                                Cobro MP {m.payment_id}: {pesos(m.amount)} · {m.status === 'approved' ? 'aprobado' : m.status} · {m.origin === 'instore' ? 'QR' : m.origin} · {hora(m.paid_at)}
                            </span>
                        ))}
                        {t.paymentType === 3 && t.mp.length === 0 && <span style={{ color: C.sobra }}>Sin cobro de MP vinculado</span>}
                        <button className="btn btn-link btn-sm p-0" onClick={() => navigateTo(`/purchase?id=${t.purchaseId}`)}>Abrir la venta</button>
                    </div>
                </td>
            </tr>
        );
    }

    renderVentas() {
        const { tickets, lines } = this.state.data;
        const f = this.state.filtros;
        const lista = filtrarTickets(tickets, f);
        const lectores = lines.filter((l) => l.kind === 'lote');
        const origenes = [...new Set(tickets.map((t) => t.origin))];
        const medios = [...new Set(tickets.map((t) => t.paymentType))].sort((a, b) => a - b);
        const hayFiltro = f.ids || f.medio || f.origen || f.lector || f.estado || f.texto;

        const select = (campo, opciones, todos) => (
            <select className="form-control form-control-sm" style={{ width: 'auto' }} value={f[campo]} onChange={(e) => this.setFiltro(campo, e.target.value)}>
                <option value="">{todos}</option>
                {opciones.map(([valor, texto]) => <option key={valor} value={valor}>{texto}</option>)}
            </select>
        );

        return (
            <div className="card card-small mb-4" ref={this.refs_.ventas}>
                <div className="card-header border-bottom d-flex flex-wrap justify-content-between align-items-center" style={{ gap: 8 }}>
                    <div>
                        <span style={{ fontWeight: 600 }}>Ventas de la caja</span>
                        <span style={{ color: C.tenue, marginLeft: 8, fontSize: 13 }}>
                            {lista.length === tickets.length ? `${tickets.length}` : `${lista.length} de ${tickets.length}`} · cobrado {pesos(totalCobrado(lista))}
                        </span>
                    </div>
                    <div style={{ fontSize: 12, color: C.tenue }}>La hora es la de la venta; una cuenta de mesa se cobra después.</div>
                </div>
                <div className="card-body py-2 border-bottom">
                    <div className="d-flex flex-wrap align-items-center" style={{ gap: 8 }}>
                        {select('medio', medios.map((m) => [String(m), MEDIOS[m] || m]), 'Todos los medios')}
                        {select('origen', origenes.map((o) => [o, ORIGENES[o] || o]), 'Todos los orígenes')}
                        {lectores.length > 0 && select('lector', [...lectores.map((l) => [String(l.pos_device_id), l.label]), ['sin', 'Tarjeta sin lector']], 'Todos los lectores')}
                        {select('estado', [['cobradas', 'Cobradas'], ['anuladas', 'Anuladas'], ['pendientes', 'Pago pendiente'], ['cambiadas', 'Cambiaron después del cierre'], ['sin_facturar', 'Sin facturar']], 'Todos los estados')}
                        <input
                            type="search"
                            className="form-control form-control-sm"
                            style={{ width: 220 }}
                            placeholder="Buscar producto, cliente, venta…"
                            value={f.texto}
                            onChange={(e) => this.setFiltro('texto', e.target.value)}
                        />
                        {hayFiltro && <button className="btn btn-sm btn-link" onClick={() => this.setState({ filtros: FILTROS_VACIOS })}>Quitar filtros</button>}
                    </div>
                    {f.ids && (
                        <div style={{ fontSize: 13, color: C.info, marginTop: 6 }}>
                            Mostrando las ventas de: <strong>{f.idsLabel}</strong>
                        </div>
                    )}
                </div>
                <div className="card-body p-0">
                    <div style={{ overflowX: 'auto' }}>
                        <table className="table mb-0" style={{ fontSize: 14 }}>
                            <thead className="bg-light">
                                <tr>
                                    <th>Hora</th>
                                    <th>Venta</th>
                                    <th>Origen</th>
                                    <th>Cliente / detalle</th>
                                    <th>Medio</th>
                                    <th className="text-right">Monto</th>
                                    <th>Estado</th>
                                    <th>Factura</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {lista.length === 0 && (
                                    <tr><td colSpan="9" style={{ textAlign: 'center', color: C.tenue, padding: 18 }}>
                                        {tickets.length === 0 ? 'Esta caja no tiene ventas.' : 'Ninguna venta con estos filtros.'}
                                    </td></tr>
                                )}
                                {lista.map((t) => {
                                    const abierto = !!this.state.abiertos[t.ticketId];
                                    const resumen = t.lines.map((l) => (l.quantity > 1 ? `${l.quantity}× ${l.name}` : l.name)).join(', ');
                                    const estado = estadoTicket(t);
                                    const lector = t.paymentType === 5 ? (this.nombreLector(t.pos_device_id) || 'sin lector') : null;
                                    return (
                                        <React.Fragment key={t.ticketId}>
                                            <tr
                                                onClick={() => this.toggleTicket(t.ticketId)}
                                                style={{ cursor: 'pointer', background: abierto ? C.fila : undefined, opacity: t.counted || !MEDIOS_PLATA.includes(t.paymentType) ? 1 : 0.65 }}
                                            >
                                                <td style={num}>{hora(t.sold_at)}</td>
                                                <td style={num}>#{t.purchaseId}</td>
                                                <td style={{ whiteSpace: 'nowrap' }}>{t.origin_label}</td>
                                                <td style={{ maxWidth: 340 }}>
                                                    {t.customer && <div style={{ fontWeight: 600 }}>{t.customer}</div>}
                                                    <div style={{ fontSize: 13, color: C.tenue, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={resumen}>{resumen || '—'}</div>
                                                </td>
                                                <td style={{ whiteSpace: 'nowrap' }}>
                                                    {MEDIOS[t.paymentType] || t.paymentType}
                                                    {lector && <div style={{ fontSize: 12, color: t.pos_device_id == null ? C.sobra : C.tenue }}>{lector}</div>}
                                                </td>
                                                <td className="text-right" style={{ ...num, textDecoration: t.cancelled ? 'line-through' : undefined }}>
                                                    {pesos(t.total)}
                                                    {(t.store_credit_applied > 0 || t.gift_card_applied > 0) && (
                                                        <div style={{ fontSize: 12, color: C.tenue }}>+{pesos(t.store_credit_applied + t.gift_card_applied)} sin plata</div>
                                                    )}
                                                </td>
                                                <td style={{ whiteSpace: 'nowrap' }}>
                                                    {estado === 'Cobrada' && <span style={{ color: C.bien }}>Cobrada</span>}
                                                    {estado === 'Anulada' && <Pill color={C.malo} fondo={C.maloFondo}>Anulada</Pill>}
                                                    {estado === 'Pago pendiente' && <Pill color={C.sobra} fondo={C.sobraFondo}>Pago pendiente</Pill>}
                                                    {estado === 'No entra al arqueo' && <span style={{ color: C.tenue }}>—</span>}
                                                    {t.changes && t.changes.length > 0 && <div><Pill color={C.malo} fondo={C.maloFondo} title={t.changes.join(' · ')}>Cambió tras el cierre</Pill></div>}
                                                </td>
                                                <td style={{ whiteSpace: 'nowrap', fontSize: 13, color: t.invoiceStatus === 0 && t.counted && t.total > 0 ? C.sobra : C.tenue }}>
                                                    {FACTURA[t.invoiceStatus] || t.invoiceStatus}
                                                </td>
                                                <td style={{ color: C.tenue }}>{abierto ? '▾' : '▸'}</td>
                                            </tr>
                                            {abierto && this.renderDetalleTicket(t)}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        );
    }

    renderResumen() {
        const { tickets } = this.state.data;
        const m = porOrigenYMedio(tickets);
        const s = sinPlata(tickets);
        const hayAlgoSinPlata = s.descuentos || s.saldoAFavor || s.giftCards || s.cuentaEquipo || s.incluidos || s.premios;
        if (m.rows.length === 0 && !hayAlgoSinPlata) return null;

        return (
            <div className="row">
                {m.rows.length > 0 && (
                    <div className="col-lg-7 mb-4">
                        <div className="card card-small h-100">
                            <div className="card-header border-bottom"><span style={{ fontWeight: 600 }}>Cobrado por origen y medio</span></div>
                            <div className="card-body p-0">
                                <div style={{ overflowX: 'auto' }}>
                                    <table className="table mb-0" style={{ fontSize: 14 }}>
                                        <thead className="bg-light">
                                            <tr>
                                                <th>Origen</th>
                                                {m.medios.map((medio) => <th key={medio} className="text-right">{MEDIOS[medio]}</th>)}
                                                <th className="text-right">Total</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {m.rows.map((r) => (
                                                <tr key={r.origin}>
                                                    <td>
                                                        <button className="btn btn-link btn-sm p-0" onClick={() => this.setState({ filtros: { ...FILTROS_VACIOS, origen: r.origin, estado: 'cobradas' } }, () => this.scrollA('ventas'))}>
                                                            {r.label}
                                                        </button>
                                                        <span style={{ fontSize: 12, color: C.tenue }}> · {r.cantidad}</span>
                                                    </td>
                                                    {m.medios.map((medio) => <td key={medio} className="text-right" style={num}>{r.porMedio[medio] ? pesos(r.porMedio[medio]) : '—'}</td>)}
                                                    <td className="text-right" style={{ ...num, fontWeight: 600 }}>{pesos(r.total)}</td>
                                                </tr>
                                            ))}
                                            <tr style={{ fontWeight: 600 }}>
                                                <td>Total</td>
                                                {m.medios.map((medio) => <td key={medio} className="text-right" style={num}>{pesos(m.totales[medio])}</td>)}
                                                <td className="text-right" style={num}>{pesos(m.total)}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                {hayAlgoSinPlata ? (
                    <div className="col-lg-5 mb-4">
                        <div className="card card-small h-100">
                            <div className="card-header border-bottom">
                                <span style={{ fontWeight: 600 }}>Entregado sin que entrara plata</span>
                            </div>
                            <div className="card-body" style={{ fontSize: 14 }}>
                                {[
                                    [s.descuentos, `Descuentos (${s.descuentosCantidad} ${s.descuentosCantidad === 1 ? 'venta' : 'ventas'})`],
                                    [s.saldoAFavor, 'Saldo a favor usado'],
                                    [s.giftCards, 'Gift cards usadas'],
                                    [s.cuentaEquipo, 'Consumo a cuenta del equipo'],
                                ].filter(([v]) => v > 0).map(([v, texto]) => (
                                    <div key={texto} className="d-flex justify-content-between" style={{ padding: '3px 0' }}>
                                        <span style={{ color: C.tenue }}>{texto}</span><span style={num}>{pesos(v)}</span>
                                    </div>
                                ))}
                                {s.premios > 0 && <div className="d-flex justify-content-between" style={{ padding: '3px 0' }}><span style={{ color: C.tenue }}>Premios y cortesías canjeados</span><span>{s.premios}</span></div>}
                                {s.incluidos > 0 && <div className="d-flex justify-content-between" style={{ padding: '3px 0' }}><span style={{ color: C.tenue }}>Consumos incluidos en eventos</span><span>{s.incluidos}</span></div>}
                                {s.codigos.length > 0 && (
                                    <div style={{ marginTop: 8, fontSize: 13 }}>
                                        {s.codigos.map((c) => (
                                            <div key={c.code} className="d-flex justify-content-between" style={{ color: C.tenue }}>
                                                <span>{c.code} · {c.cantidad}</span><span style={num}>{pesos(c.monto)}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                ) : null}
            </div>
        );
    }

    // ── Movimientos, cuenta del equipo, MP, fuera de caja ─────────────────────────────────────

    renderMovimientos() {
        const { movements, employee_account: equipo } = this.state.data;
        if (movements.length === 0 && equipo.length === 0) return null;

        return (
            <div className="row" ref={this.refs_.movimientos}>
                <div className="col-lg-6 mb-4">
                    <div className="card card-small h-100">
                        <div className="card-header border-bottom"><span style={{ fontWeight: 600 }}>Movimientos del cajón</span></div>
                        <div className="card-body p-0">
                            {movements.length === 0 ? (
                                <div style={{ padding: 16, color: C.tenue }}>Sin retiros, gastos ni ingresos.</div>
                            ) : (
                                <table className="table mb-0" style={{ fontSize: 14 }}>
                                    <tbody>
                                        {movements.map((m) => {
                                            const sale = MOVIMIENTOS_SALIDA.includes(m.type);
                                            return (
                                                <tr key={m.id}>
                                                    <td style={num}>{hora(m.created_at)}</td>
                                                    <td>
                                                        <div style={{ fontWeight: 600 }}>{MOVIMIENTOS[m.type] || m.type}</div>
                                                        <div style={{ fontSize: 13, color: m.reason ? C.fuerte : C.malo }}>{m.reason || 'sin motivo'}</div>
                                                        <div style={{ fontSize: 12, color: C.tenue }}>{m.created_by_name || m.created_by}</div>
                                                    </td>
                                                    <td className="text-right" style={{ ...num, color: sale ? C.malo : C.bien }}>{sale ? '−' : '+'}{pesos(m.amount)}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                </div>
                <div className="col-lg-6 mb-4">
                    <div className="card card-small h-100">
                        <div className="card-header border-bottom"><span style={{ fontWeight: 600 }}>Cuenta del equipo</span></div>
                        <div className="card-body p-0">
                            {equipo.length === 0 ? (
                                <div style={{ padding: 16, color: C.tenue }}>Sin consumos ni pagos de la cuenta del equipo.</div>
                            ) : (
                                <table className="table mb-0" style={{ fontSize: 14 }}>
                                    <tbody>
                                        {equipo.map((m) => (
                                            <tr key={m.id}>
                                                <td style={num}>{hora(m.created_at)}</td>
                                                <td>
                                                    <div style={{ fontWeight: 600 }}>{m.employee}</div>
                                                    <div style={{ fontSize: 13, color: C.tenue }}>
                                                        {CUENTA_EQUIPO[m.type] || m.type}
                                                        {m.payment_type ? ` · ${MEDIOS[m.payment_type]}` : ''}
                                                        {m.reference ? ` · ref. ${m.reference}` : ''}
                                                    </div>
                                                </td>
                                                <td className="text-right" style={num}>{pesos(Math.abs(m.amount))}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                            <div style={{ fontSize: 12, color: C.tenue, padding: '8px 16px' }}>
                                El consumo a cuenta no es plata y no entra a ningún total; los pagos sí, en el medio con el que se hicieron.
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    renderFueraDeCaja() {
        const { unsealed, mp_unlinked: mpSueltos } = this.state.data;
        if (unsealed.length === 0 && mpSueltos.length === 0) return null;
        return (
            <div className="row">
                {unsealed.length > 0 && (
                    <div className="col-lg-6 mb-4" ref={this.refs_.sinCaja}>
                        <div className="card card-small h-100">
                            <div className="card-header border-bottom">
                                <span style={{ fontWeight: 600 }}>Cobradas sin entrar a ninguna caja</span>
                            </div>
                            <div className="card-body p-0">
                                <table className="table mb-0" style={{ fontSize: 14 }}>
                                    <tbody>
                                        {unsealed.map((u) => (
                                            <tr key={u.ticketId}>
                                                <td style={num}>{hora(u.sold_at)}</td>
                                                <td>
                                                    <button className="btn btn-link btn-sm p-0" onClick={() => navigateTo(`/purchase?id=${u.purchaseId}`)}>Venta #{u.purchaseId}</button>
                                                    <div style={{ fontSize: 12, color: C.tenue }}>{u.channel} · {MEDIOS[u.paymentType]}</div>
                                                </td>
                                                <td className="text-right" style={num}>{pesos(u.totalPrice)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}
                {mpSueltos.length > 0 && (
                    <div className="col-lg-6 mb-4" ref={this.refs_.mp}>
                        <div className="card card-small h-100">
                            <div className="card-header border-bottom">
                                <span style={{ fontWeight: 600 }}>Cobros con el QR de MP sin venta</span>
                            </div>
                            <div className="card-body p-0">
                                <table className="table mb-0" style={{ fontSize: 14 }}>
                                    <tbody>
                                        {mpSueltos.map((m) => (
                                            <tr key={m.payment_id}>
                                                <td style={num}>{hora(m.paid_at)}</td>
                                                <td>
                                                    <div>Cobro {m.payment_id}</div>
                                                    {m.review_reason && <div style={{ fontSize: 12, color: C.tenue }}>{m.review_reason.replace(/_/g, ' ')}</div>}
                                                </td>
                                                <td className="text-right" style={num}>{pesos(m.amount)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    renderContenido() {
        if (Number(this.state.role) !== ROLE_ADMIN) {
            return <div className="alert alert-secondary">El control de caja es sólo para administradores.</div>;
        }
        if (this.state.loading) {
            return (
                <div className="containerLoader" style={{ display: 'block', position: 'static', width: '100%', minHeight: 240 }}>
                    <div className="loader"></div>
                </div>
            );
        }
        if (this.state.error) {
            return (
                <div className="card card-small mb-4">
                    <div className="card-body">
                        <div style={{ color: C.malo, marginBottom: 10 }}>{this.state.error}</div>
                        <button className="btn btn-sm btn-white" onClick={() => navigateTo('/caja')}>Volver al historial</button>
                    </div>
                </div>
            );
        }
        return (
            <div style={{ opacity: this.state.refreshing ? 0.7 : 1, transition: 'opacity .15s' }}>
                {this.renderEncabezado()}
                {this.renderAlertas()}
                {this.renderLineas()}
                {this.renderTransferencias()}
                {this.renderResumen()}
                {this.renderVentas()}
                {this.renderMovimientos()}
                {this.renderFueraDeCaja()}
            </div>
        );
    }

    render() {
        if (!this.state.checked) {
            return <div></div>;
        }
        return (
            <div className="container-fluid">
                <div className="row">
                    <SidebarComponent itemSelected={12} role={this.state.role} />
                    <main className="main-content col-lg-10 col-md-10 col-sm-12 p-0 offset-lg-2 offset-md-2">
                        <NavbarComponent role={this.state.role} />
                        <div className="main-content-container container-fluid px-4 mb-4">
                            <HeaderComponent title="Caja" subtitle="Control de caja" />
                            {this.renderContenido()}
                        </div>
                    </main>
                </div>
            </div>
        );
    }
}

export default CashControlPage;
